import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  ChevronDownIcon, 
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';
import { useHierarchyOperations } from '@/hooks/useHierarchyOperations';
import { HierarchyActionModals } from '@/components/hierarchy/HierarchyActionModals';
import { HierarchyNodeActions } from '@/components/hierarchy/HierarchyNodeActions';
import { HierarchyImportButton } from '@/components/hierarchy/HierarchyImportButton';

interface HierarchyNode {
  id: string;
  name: string;
  level_type: string;
  parent_id?: string;
  level: number;
  status: string;
  children?: HierarchyNode[];
  expanded?: boolean;
}

// Removed TenantExtension interface - functionality not needed

const GeographicHierarchyPage: React.FC = () => {
  const { t } = useLanguage();
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  // Removed tenantExtensions - functionality not needed
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showMoveForm, setShowMoveForm] = useState(false);
  const [draggedNode, setDraggedNode] = useState<HierarchyNode | null>(null);
  const [dragOverNode, setDragOverNode] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    level_type: 'WORLDWIDE',
    parent_id: ''
  });
  const [moveData, setMoveData] = useState({
    newParentId: ''
  });

  useEffect(() => {
    loadHierarchy();
  }, []);

  const loadHierarchy = async (): Promise<void> => {
    try {
      setLoading(true);
      // Get platform ID from user context or use default
      // Use new tenant-aware API endpoint
      const response = await api.get('/hierarchy/geographic');
      const data = response.data.data || response.data;
      const treeData = buildHierarchyTree(data);
      setHierarchy(treeData);
    } catch (err) {
      setError('Failed to load geographic hierarchy');
      console.error('Error loading hierarchy:', err);
    } finally {
      setLoading(false);
    }
  };

  // Use the common hierarchy operations hook
  const {
    selectedNode,
    setSelectedNode,
    handleDeleteClick,
  } = useHierarchyOperations({
    hierarchyType: 'geographic',
    onRefresh: loadHierarchy,
    onError: setError,
  });

  const buildHierarchyTree = (data: any[]): HierarchyNode[] => {
    const map = new Map();
    const roots: HierarchyNode[] = [];

    data.forEach(item => {
      map.set(item.id, { ...item, children: [], expanded: false });
    });

    data.forEach(item => {
      const node = map.get(item.id);
      if (item.parent_id && map.has(item.parent_id)) {
        const parent = map.get(item.parent_id);
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  const toggleNode = (nodeId: string, nodes: HierarchyNode[]): HierarchyNode[] => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return { ...node, expanded: !node.expanded };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: toggleNode(nodeId, node.children) };
      }
      return node;
    });
  };

  const handleToggleNode = (nodeId: string) => {
    setHierarchy(prev => toggleNode(nodeId, prev));
  };

  // Helper function to determine child level type based on parent
  const getChildLevelType = (parentLevelType: string): string => {
    const levelTypeMap: { [key: string]: string } = {
      'WORLDWIDE': 'CONTINENT',
      'CONTINENT': 'COUNTRY', 
      'COUNTRY': 'STATE_REGION',
      'STATE_REGION': 'CITY',
      'CITY': 'CITY' // Keep same type for deeper levels
    };
    return levelTypeMap[parentLevelType] || 'STATE_REGION';
  };

  const handleAddNode = () => {
    const childLevelType = selectedNode ? getChildLevelType(selectedNode.level_type) : 'STATE_REGION';
    
    setFormData({
      name: '',
      level_type: childLevelType,
      parent_id: selectedNode?.id || ''
    });
    setShowAddForm(true);
    setShowEditForm(false);
  };

  const handleEditNode = (node: HierarchyNode) => {
    setSelectedNode(node);
    setFormData({
      name: node.name,
      level_type: node.level_type,
      parent_id: node.parent_id || ''
    });
    setShowEditForm(true);
    setShowAddForm(false);
    setShowMoveForm(false);
  };

  const handleCopyNode = async (node: HierarchyNode) => {
    try {
      // Create a new node with the same properties but a different name
      const copyName = `${node.name} (Copy)`;
      const response = await api.post('/hierarchy/geographic', {
        name: copyName,
        level_type: node.level_type,
        parent_id: node.parent_id,
        level: node.level
      });
      
      if (response.status === 201) {
        await loadHierarchy();
        setSelectedNode(null);
      }
    } catch (err) {
      setError('Failed to copy hierarchy node');
      console.error('Error copying node:', err);
    }
  };

  // Removed handleMoveNode as it's no longer used (replaced by drag and drop)


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (showAddForm) {
        // Auto-determine level type based on whether it's a root or child node
        let level_type = 'WORLDWIDE'; // Default for root nodes
        let parent_id = null;
        
        if (selectedNode) {
          // Adding child node - determine based on parent
          parent_id = selectedNode.id;
          
          // Determine level type based on parent's level type
          switch (selectedNode.level_type) {
            case 'WORLDWIDE':
              level_type = 'CONTINENT';
              break;
            case 'CONTINENT':
              level_type = 'COUNTRY';
              break;
            case 'COUNTRY':
              level_type = 'STATE_REGION';
              break;
            case 'STATE_REGION':
              level_type = 'CITY';
              break;
            default:
              level_type = 'CITY';
          }
        }
        
        const response = await api.post('/hierarchy/geographic', {
          name: formData.name,
          level_type: level_type,
          parent_id: parent_id,
          level: selectedNode ? (selectedNode.level || 0) + 1 : 1
        });
        
        if (response.status === 201) {
          await loadHierarchy();
          setShowAddForm(false);
          setSelectedNode(null);
        }
      } else if (showEditForm && selectedNode) {
        // First update the node properties
        const updateResponse = await api.put(`/hierarchy/geographic/${selectedNode.id}`, {
          name: formData.name,
          status: 'ACTIVE'
        });
        
        if (updateResponse.status === 200) {
          await loadHierarchy();
          setShowEditForm(false);
          setSelectedNode(null);
        }
      }
      
      setFormData({ name: '', level_type: 'WORLDWIDE', parent_id: '' });
    } catch (err) {
      setError('Failed to save geographic hierarchy node');
      console.error('Error saving hierarchy node:', err);
    }
  };

  const handleMoveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedNode) {
        const response = await api.put(`/hierarchy/geographic/${selectedNode.id}/parent`, {
          newParentId: moveData.newParentId || null
        });
        
        if (response.status === 200) {
          await loadHierarchy();
          setShowMoveForm(false);
          setSelectedNode(null);
        }
      }
    } catch (err) {
      setError('Failed to move geographic hierarchy node');
      console.error('Error moving node:', err);
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent, node: HierarchyNode) => {
    setDraggedNode(node);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', node.id);
  };

  const handleDragOver = (e: React.DragEvent, node: HierarchyNode) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverNode(node.id);
    
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    // Auto-expand the node if it has children after 2 seconds of hovering
    if (node.children && node.children.length > 0 && !node.expanded) {
      const timeout = setTimeout(() => {
        const updatedHierarchy = expandNodeInHierarchy(hierarchy, node.id);
        setHierarchy(updatedHierarchy);
      }, 2000);
      setHoverTimeout(timeout);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're actually leaving the element
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverNode(null);
      // Clear the hover timeout when leaving the element
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
    }
  };

  const handleDrop = async (e: React.DragEvent, targetNode: HierarchyNode) => {
    e.preventDefault();
    
    if (!draggedNode || draggedNode.id === targetNode.id) {
      setDraggedNode(null);
      setDragOverNode(null);
      return;
    }

    // Prevent dropping a node onto its own descendant
    if (isDescendant(draggedNode, targetNode)) {
      setError('Cannot move a node to its own descendant');
      setDraggedNode(null);
      setDragOverNode(null);
      return;
    }

    try {
      const response = await api.put(`/hierarchy/geographic/${draggedNode.id}/parent`, {
        newParentId: targetNode.id
      });
      
      if (response.status === 200) {
        // Clear any pending hover timeout
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          setHoverTimeout(null);
        }
        
        // Reload the hierarchy to get the updated structure with the moved node
        await loadHierarchy();
        
        // Re-expand the target node after reload to show the moved node
        setTimeout(() => {
          const finalHierarchy = expandNodeInHierarchy(hierarchy, targetNode.id);
          setHierarchy(finalHierarchy);
        }, 100);
        
        setSelectedNode(null);
      }
    } catch (err) {
      setError('Failed to move geographic hierarchy node');
      console.error('Error moving geographic node:', err);
    }
    
    setDraggedNode(null);
    setDragOverNode(null);
  };

  const handleDragEnd = () => {
    setDraggedNode(null);
    setDragOverNode(null);
    // Clear any pending hover timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  // Helper function to expand a node in the hierarchy
  const expandNodeInHierarchy = (nodes: HierarchyNode[], nodeId: string): HierarchyNode[] => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return { ...node, expanded: true };
      }
      if (node.children) {
        return { ...node, children: expandNodeInHierarchy(node.children, nodeId) };
      }
      return node;
    });
  };

  // Helper function to check if a node is a descendant of another
  const isDescendant = (ancestor: HierarchyNode, potentialDescendant: HierarchyNode): boolean => {
    if (!ancestor.children) return false;
    
    for (const child of ancestor.children) {
      if (child.id === potentialDescendant.id) return true;
      if (isDescendant(child, potentialDescendant)) return true;
    }
    return false;
  };

  const renderHierarchyNode = (node: HierarchyNode, level: number = 0) => (
    <div key={node.id} className="ml-4">
      <div 
        className={`flex items-center space-x-2 py-2 px-3 rounded-lg hover:bg-[var(--color-secondary-bg)] transition-colors ${
          selectedNode?.id === node.id ? 'bg-[var(--color-info)]/10 border border-[var(--color-info)]/20' : ''
        } ${
          dragOverNode === node.id ? 'bg-[var(--color-success)]/10 border-2 border-[var(--color-success)]/20' : ''
        } ${
          draggedNode?.id === node.id ? 'opacity-50' : ''
        }`}
        draggable
        onDragStart={(e) => handleDragStart(e, node)}
        onDragOver={(e) => handleDragOver(e, node)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, node)}
        onDragEnd={handleDragEnd}
      >
        <button
          onClick={() => handleToggleNode(node.id)}
          className="p-1 hover:bg-[var(--color-secondary-bg)] rounded"
        >
          {node.children && node.children.length > 0 ? (
            node.expanded ? (
              <ChevronDownIcon className="h-4 w-4 text-[var(--color-secondary-text)]" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-[var(--color-secondary-text)]" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </button>
        
        <div className={`w-2 h-2 rounded-full ${
          node.status === 'ACTIVE' ? 'bg-[var(--color-success)]' : 'bg-[var(--color-secondary-text)] opacity-50'
        }`} />
        
        <span 
          className="flex-1 text-sm font-medium text-[var(--color-surface-text)] cursor-pointer hover:text-[var(--color-info)]"
          onClick={() => setSelectedNode(node)}
        >
          {node.name}
        </span>
        
        <span className="text-xs text-[var(--color-secondary-text)] bg-[var(--color-secondary-bg)] px-2 py-1 rounded">
          {node.level_type}
        </span>
        
        <HierarchyNodeActions
          node={node}
          onEdit={handleEditNode}
          onDelete={handleDeleteClick}
          onCopy={handleCopyNode}
        />
      </div>
      
      {node.expanded && node.children && node.children.length > 0 && (
        <div className="ml-4">
          {node.children.map(child => renderHierarchyNode(child, level + 1))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[var(--color-surface-text)]">{t('pages.geographicHierarchy.loadingHierarchy')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[var(--color-error)]">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
          {t('pages.geographicHierarchy.title')}
        </h1>
        <p className="text-[var(--color-secondary-text)]">
          {t('pages.geographicHierarchy.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Hierarchy Structure */}
        <div className="lg:col-span-2">
          <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-[var(--color-surface-text)]">
                {t('pages.geographicHierarchy.geographicHierarchyStructure')}
              </h2>
              <div className="flex items-center space-x-3">
                <HierarchyImportButton
                  hierarchyType="GEOGRAPHIC"
                  onImport={loadHierarchy}
                />
                <button
                  onClick={handleAddNode}
                  className="flex items-center space-x-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-3 py-2 rounded-md hover:bg-opacity-90 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>
                    {selectedNode 
                      ? `Add Child to "${selectedNode.name}"` 
                      : t('hierarchy.addNode')
                    }
                  </span>
                </button>
              </div>
            </div>
            
            <div className="mb-2 text-sm text-[var(--color-surface-text)] opacity-75">
              💡 Click on a node to select it, then click &quot;Add Node&quot; to create a child node
              <br />
              🖱️ Drag and drop nodes to move them to different parents (nodes auto-expand while dragging)
            </div>
            <div className="space-y-2">
              {hierarchy.map(node => renderHierarchyNode(node))}
            </div>
          </div>
        </div>

        {/* Actions Panel */}
        <div className="space-y-6">
          {/* Selected Node Info */}
          {selectedNode && (
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-4 border border-[var(--color-border)]">
              <h3 className="text-md font-medium text-[var(--color-surface-text)] mb-2">
                Selected Node Info
              </h3>
              <div className="space-y-2 text-sm text-[var(--color-surface-text)]">
                <div><strong>Name:</strong> {selectedNode.name}</div>
                <div><strong>Level Type:</strong> {selectedNode.level_type}</div>
                <div><strong>Children:</strong> {selectedNode.children?.length || 0}</div>
              </div>
            </div>
          )}

          {/* Add/Edit Form */}
          {(showAddForm || showEditForm) && (
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-4 border border-[var(--color-border)]">
              <h3 className="text-md font-medium text-[var(--color-surface-text)] mb-4">
                {showAddForm ? 'Add New Node' : 'Edit Node'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Simple form - only name required for both root and child nodes */}
                <div>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                    placeholder="Enter node name"
                    required
                    autoFocus
                  />
                </div>
                
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors"
                  >
                    {showAddForm ? 'Add' : 'Update'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setShowEditForm(false);
                      setSelectedNode(null);
                    }}
                    className="flex-1 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Move Form */}
          {showMoveForm && selectedNode && (
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-4 border border-[var(--color-border)]">
              <h3 className="text-md font-medium text-[var(--color-surface-text)] mb-4">
                Move Node: {selectedNode.name}
              </h3>
              
              <form onSubmit={handleMoveSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                    Select New Parent:
                  </label>
                  <select
                    value={moveData.newParentId}
                    onChange={(e) => setMoveData({ newParentId: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
                  >
                    <option value="">Root Level (No Parent)</option>
                    {hierarchy
                      .filter(node => node.id !== selectedNode.id) // Don't allow selecting self as parent
                      .map(node => (
                        <option key={node.id} value={node.id}>
                          {node.name} ({node.level_type})
                        </option>
                      ))}
                  </select>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors"
                  >
                    Move
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoveForm(false);
                      setSelectedNode(null);
                    }}
                    className="flex-1 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <HierarchyActionModals
            selectedNode={selectedNode}
            hierarchy={hierarchy}
          />
        </div>
      </div>

      {/* Removed Tenant Extensions section - functionality not needed */}
    </div>
  );
};

export default GeographicHierarchyPage;
